// src/LearningContent.js
import React from 'react';

// You can add CSS classes here and create a LearningContent.css file for styling
const topicContentStyle = {
    padding: '15px',
    border: '1px solid #eee',
    borderRadius: '5px',
    backgroundColor: '#f9f9f9',
    lineHeight: '1.6'
};

const headingStyle = {
    marginTop: '0',
    marginBottom: '15px',
    color: '#333'
};

const listStyle = {
    paddingLeft: '20px'
};

const listItemStyle = {
    marginBottom: '10px'
};

const subheadingStyle = {
    marginTop: '25px',
    marginBottom: '10px',
    color: '#333',
    borderBottom: '1px solid #eee', // Optional separator
    paddingBottom: '5px' // Optional spacing
};

// --- Investment 101 Content ---
function Investment101Content() {
  return (
    <div className="learning-topic-content" style={topicContentStyle}> {/* Optional wrapper class */}
      <h2 style={headingStyle}>Investing 101: The Basics</h2>
      <p>
        Welcome to the world of investing! Getting started might seem complex, but understanding a few key principles can make a big difference in building long-term wealth. Remember, this information is for educational purposes only and not financial advice.
      </p>
      <h3 style={subheadingStyle}>Key Concepts</h3>
      <ul style={listStyle}>
        <li style={listItemStyle}>
          <strong>Compounding:</strong> Often called the "eighth wonder of the world," compounding is when your investment returns start earning their own returns. The earlier you start, the more powerful it becomes. Think of it like rolling a snowball down a hill – it grows bigger over time.
        </li>
        <li style={listItemStyle}>
          <strong>Diversification:</strong> This classic advice means "don't put all your eggs in one basket." Spreading your money across different types of investments (like stocks from different industries, bonds, possibly real estate or commodities) helps reduce your overall risk. If one investment performs poorly, others might balance it out.
        </li>
        <li style={listItemStyle}>
          <strong>Risk vs. Reward:</strong> There's usually a trade-off. Investments with the potential for higher returns (like individual stocks) typically come with higher risk (greater chance of losing value). Safer investments (like government bonds or savings accounts) usually offer lower returns. Understanding your personal comfort level with risk (your "risk tolerance") is key to choosing suitable investments.
        </li>
	<li style={listItemStyle}>
           <strong>Value vs. Growth:</strong> These are two common investment styles. <strong>Value investing</strong> focuses on finding stocks that appear priced below their fundamental worth (undervalued), often characterized by lower P/E ratios or strong financials relative to their price. The belief is the market will eventually recognize their true value. <strong>Growth investing</strong> focuses on companies expected to grow their earnings or revenue faster than the average, even if they seem expensive based on current metrics. The potential for future expansion is prioritized. Neither style is inherently superior, and they often perform differently depending on market conditions. Many investors blend approaches.
        </li>
        <li style={listItemStyle}>
          <strong>Time Horizon:</strong> How long do you plan to keep your money invested? If you need the money soon (e.g., less than 5 years), safer, less volatile investments are often preferred. If you're investing for the long term (e.g., retirement decades away), you might be able to tolerate more short-term ups and downs for the potential of higher long-term growth.
        </li>
        <li style={listItemStyle}>
          <strong>Consistency &amp; Costs:</strong> Regularly investing a set amount over time (dollar-cost averaging) can smooth out market fluctuations. Also, be mindful of fees and expenses associated with investing (brokerage fees, fund expense ratios), as they can significantly impact your returns over time.
        </li>
      </ul>
      <p>
        This is just a starting point. Read the "DeepValue Guide" for advanced concepts.
      </p>
    </div>
  );
}

// --- User Guide Content ---
function UserGuideContent() {
  // Reuse styles if defined elsewhere or add inline/CSS
  const topicContentStyle = { padding: '15px', border: '1px solid #eee', borderRadius: '5px', backgroundColor: '#f9f9f9', lineHeight: '1.6' };
  const headingStyle = { marginTop: '0', marginBottom: '15px', color: '#333' };

  return (
    <div className="learning-topic-content" style={topicContentStyle}>
      <h2 style={headingStyle}>User Guide</h2>
      <p>
        This guide provides instructions on how to use the DeepValue Portfolio Analyzer.
      </p>

      <h3>Adding Positions</h3>
      <p>
        Use the "Add Position" form, enter the ticker symbol (e.g., AAPL, VOO), the quantity, and select the type (Stock, ETF, Cash). Click "Add".
      </p>

      <h3>Editing/Deleting Positions</h3>
      <p>
        In the tables below the form, find the position you wish to modify. Click the "Edit" button to change the quantity or type, or click "Delete" to remove the position entirely.
      </p>

      <h3>Investor Profile</h3>
      <p>
        You can set your Investment Philosophy and Risk Tolerance using the dropdowns in the "Investor Profile" section. Click "Update Profile" to save your changes. This will affect Gen AI recommendations.
      </p>

      <h3>Gen AI Prompts</h3>
      <p>
        Click the "Generate Rebalance Prompt" button to copy your current portfolio summary and profile settings to the clipboard, ready to paste into a generative AI tool (like ChatGPT, Grok, Gemini) to ask for rebalancing suggestions based on your investor profile.
      </p>

      <h3>Upcoming Earnings</h3>
      <p>
         The Upcoming Earnings table displays stocks in your portfolio that has an earnings report date within the next 10 days. It provides a Buy/Sell recommendation, along with suggested "short" options trades to profit off of volatility drop post earnings.
      </p>

    </div>
  );
}

// --- DeepValue 101 Content Component ---
function DeepValue101Content() {
  return (
    <div className="learning-topic-content" style={topicContentStyle}>
      <h2 style={headingStyle}>DeepValue Guide: Advanced Concepts</h2>
      <p>
        This page introduces advanced concepts used by the DeepValue Portfolio Analyzer.
      </p>

      <h3 style={subheadingStyle}>Key Concepts</h3>
      <ul style={listStyle}>
        <li style={listItemStyle}>
          <strong>Investing vs. Speculating:</strong> <strong>Investing</strong> is purchasing undervalued stocks of established companies with a focus on capital preservation and adequate returns. <strong>Speculating</strong> is purchasing assets with disregard of underlying value, such as stocks of companies with negative earnings, or commodities where the price is driven primarily by supply & demand, such as gold, oil, and cryptocurrency. Speculation should be kept to less than 5% of your portfolio. The DeepValue Portfolio Analyzer's primary focus is on investing.
        </li>
        <li style={listItemStyle}>
          <strong>Margin of Safety:</strong> Buying stock at a price significantly below intrinsic value to minimize the risk of further price declines.
        </li>
        <li style={listItemStyle}>
          <strong>Price to Earnings Ratio (PE):</strong> Price to earnings ratio is the cornerstone of valuation analysis. In general, the stock of a company with PE below its peers is considered “cheap” while the stock of a company with PE above its peers is considered “expensive”. The baseline PE varies depending on the sector, but a PE of 20 is commonly used as the benchmark for companies in the S&P 500. Airline and telecom companies routinely have PE less than 10 while tech companies have PE around 25 (the higher PE is due to the fact that tech companies are expected to have higher rate of earnings growth, which is baked into the stock price).
        </li>
        <li style={listItemStyle}>
          <strong>Forward Price to Earnings Ratio (FPE):</strong> The forecasted PE 12 months from now. If the FPE is significantly lower than the PE, this is a positive sign, since it means the company is on a healthy trajectory for earnings growth. Conversely, if the FPE is significantly higher than the PE, this could be a major red flag, since it means the company is anticipating lowered profits and is possibly on the path to decline.
        </li>
        <li style={listItemStyle}>
          <strong>Relative Strength Index (RSI):</strong> RSI is a technical indicator that ranges from 0 to 100. A score above 70 indicates a stock is overbought. A score below 30 indicates a stock is oversold. A score of 50 is neutral. RSI is normally calculated using a 14-day window.
        </li>
        <li style={listItemStyle}>
          <strong>Analyst Consensus:</strong> The average buy/sell rating given by financial analysts that cover a particular stock. Due to the effects of “wisdom of the crowd” and “self-fulfilling prophecy”, the analyst consensus exerts a non-trivial amount of influence on the price of a stock. However, analyst consensus can be entirely wrong at times due to outdated information, so it should be taken with a grain of salt.
	</li>
        <li style={listItemStyle}>
          <strong>Price Target:</strong> The forecasted stock price 12 months from now as given by financial analysts.
        </li>
      </ul>

      <h3 style={subheadingStyle}>DFV Score</h3>
      <p>
        DFV score is a proprietary scoring system used for identifying deeply undervalued stocks based on fundamental analysis and technical indicators. A high, positive DFV score is awarded to a stock with low PE, low FPE, improving PE trend, low RSI, and market price far below analyst price targets. Conversely, a negative DFV score is assigned to an overvalued and/or overbought stock. A DFV score of 0 is neutral, indicating the stock is trading at a fair value. Buying stocks with a high DFV scores is a systematic way to build in a margin of safety on stock purchases.
      </p>
      <p>
        Roughly speaking, DFV score can be interpreted as follows.
      </p>
      <ul style={listStyle}>
        {/* Use HTML entities for less-than/greater-than */}
        <li style={listItemStyle}>&lt; -4: Strong Sell</li>
        <li style={listItemStyle}>-4 to 0: Sell</li>
        <li style={listItemStyle}>0 to 4: Hold</li>
        <li style={listItemStyle}>4 to 8: Buy</li>
        <li style={listItemStyle}>&gt; 8: Strong Buy</li>
      </ul>
      <p>
        However, it is important to note that a stock can have sustained periods of very high or very low DFV scores during periods of volatility. Therefore, DFV score is best used as a screening tool for comparing the relative attractiveness of 2 or more stock candidates. For example, in a prolonged market downturn, a stock with DFV score of 6 will generally fall less than a stock with DFV score of 2 and also eventually make a bigger rebound.
      </p>
      <p>
        DFV score tends to deviate away from 0 in the time between quarterly earnings reports due to entropy, but immediately after earnings, it tends to revert back towards 0. This is because the stock get a lot of media and analyst coverage on earnings day, and an undervalued stock with high DFV will get bid up (thus lowering the DFV score), while an overvalued stock with low DFV will get dumped (thus increasing the DFV score). This pattern makes the DFV score a particularly useful guide for making trades right before earnings day.
      </p>
    </div>
  );
}

// Export the components so App.js can import them
export { Investment101Content, UserGuideContent, DeepValue101Content };
